<script setup lang="ts">
import { computed, useAttrs, useTemplateRef } from "vue";

import type { Icon } from "@/model/icon";

import LoadingIndicator from "../LoadingIndicator.vue";
import SvgIcon from "../SvgIcon/SvgIcon.vue";
import { useAsyncHandler } from "./internal/useAsyncHandler";

interface Props {
  as?: "button" | "a" | "div";
  variant?: "filled" | "outlined" | "ghost" | "text";
  size?: "small" | "medium";
  color?: "primary" | "grey" | "destructive";
  fullWidth?: boolean;
  iconBefore?: Icon;
  iconAfter?: Icon;
  disabled?: boolean;
  activated?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  as: "button",
  variant: "filled",
  size: "medium",
  color: "primary",
  fullWidth: false,
  iconBefore: undefined,
  iconAfter: undefined,
  activated: undefined,
});

defineOptions({ inheritAttrs: false });
const $attrs = useAttrs();

// Allow parent to focus the button and call getBoundingClientRect
defineExpose({
  blur: () => void buttonRef.value?.blur(),
  focus: () => void buttonRef.value?.focus(),
  getBoundingClientRect: () => buttonRef.value!.getBoundingClientRect(),
});

const buttonRef = useTemplateRef<HTMLElement>("button");

const { attrs, processing } = useAsyncHandler(100, "onClick");

const aria = computed(() => {
  if (props.as === "button" && props.activated !== undefined) {
    switch ($attrs.role) {
      case "radio":
        return {
          ariaChecked: props.activated ? "true" : "false",
          tabIndex: props.activated ? 0 : -1,
        };
      case "tab":
      case "option":
        return { ariaSelected: props.activated ? "true" : "false" };
      default:
        return { ariaPressed: props.activated ? "true" : "false" };
    }
  }
  return {};
});
</script>

<template>
  <component
    :is="as"
    ref="button"
    :class="['base-button', variant, color, size, { 'full-width': fullWidth, activated }]"
    :disabled
    v-bind="{ ...attrs, ...aria }"
  >
    <SvgIcon v-if="iconBefore" :name="iconBefore" class="icon-before" aria-hidden="true" />
    <span v-if="$slots.default" class="content">
      <LoadingIndicator v-if="processing.onClick" :inverted="variant === 'filled'" />
      <slot v-else />
    </span>
    <SvgIcon v-if="iconAfter" :name="iconAfter" class="icon-after" aria-hidden="true" />
  </component>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";
@use "@/styles/mixins/typography";

.base-button {
  @include typography.medium;

  outline: revert;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  user-select: none;
  cursor: pointer;

  .icon-before,
  .icon-after {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }

  &.small {
    padding-inline: 8px;
    height: 32px;
    border-radius: 4px;
  }

  &.medium {
    padding-inline: 12px;
    height: 36px;
    border-radius: 6px;
  }

  &.full-width {
    width: 100%;
  }

  .content {
    @include utils.ellipsis;
  }

  &[class]:disabled {
    cursor: not-allowed;
  }

  &.filled {
    &[class]:disabled {
      color: colors.$text-disabled;
      background-color: colors.$background-grey-light;
    }

    &.primary {
      background-color: colors.$main;
      color: colors.$text-inverse;
      outline-offset: 3px; // Outline is the same color as the button

      &:not(:disabled, .activated) {
        &:hover {
          background-color: colors.$main-hover;
        }

        &:active {
          background-color: colors.$main-hover;
        }
      }

      &.activated {
        background-color: colors.$main;
      }
    }

    &.grey {
      background-color: colors.$alpha-5;
      color: colors.$text-subtle;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: colors.$alpha-10;
          color: colors.$text;
        }

        &:active {
          background-color: colors.$alpha-20;
          color: colors.$text;
        }
      }

      &.activated {
        background-color: colors.$background-grey;
        color: colors.$text;
      }
    }

    &.destructive {
      background-color: colors.$destructive;
      color: colors.$text-inverse;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: colors.$destructive-hover;
        }

        &:active {
          background-color: colors.$destructive-active;
        }
      }

      &.activated {
        background-color: colors.$destructive-active;
      }
    }
  }

  &.outlined {
    background-color: colors.$background-ui;

    &[class]:disabled {
      color: colors.$text-disabled;
      border: 1px solid colors.$border;
    }

    &.primary {
      color: colors.$text-subtle;
      border: 1px solid colors.$main;

      &:not(:disabled, .activated) {
        &:hover {
          color: colors.$main;
          background-color: colors.$background-grey-light;
        }

        &:active {
          color: colors.$main;
          background-color: colors.$background-grey;
        }
      }

      &.activated {
        color: colors.$main;
        background-color: colors.$background-grey;
      }
    }

    &.grey {
      color: colors.$text-subtle;
      background-color: colors.$background-ui;
      border: 1px solid colors.$border-subtle;

      &:not(:disabled, .activated) {
        &:hover {
          color: colors.$text;
          background-color: colors.$alpha-5;
          border: 1px solid colors.$border;
        }

        &:active {
          color: colors.$text;
          background-color: colors.$alpha-10;
          border: 1px solid colors.$border;
        }
      }

      &.activated {
        color: colors.$text-inverse;
        background-color: colors.$main;
        border: 1px solid colors.$main;
      }
    }

    &.destructive {
      color: colors.$destructive;
      background-color: colors.$background-ui;
      border: 1px solid currentcolor;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: colors.$destructive-hover-light;
        }

        &:active {
          background-color: colors.$destructive-active-light;
        }
      }

      &.activated {
        background-color: colors.$destructive-active-light;
      }
    }
  }

  &.ghost {
    &[class]:disabled {
      cursor: not-allowed;
      color: colors.$text-disabled;
    }

    &.primary {
      color: colors.$main;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: colors.$alpha-5;
        }

        &:active {
          background-color: colors.$alpha-10;
        }
      }

      &.activated {
        background-color: colors.$background-grey;
      }

      // We need dark-mode overrides for this variant because
      // we don't have a different $main for dark mode
      .dark-mode &:not(:disabled, .activated) {
        color: colors.$main;

        &:hover,
        &:active {
          color: colors.$text;
        }
      }
    }

    &.grey {
      color: colors.$text-subtle;

      &:not(:disabled, .activated) {
        &:hover {
          color: colors.$text;
          background-color: colors.$alpha-5;
        }

        &:active {
          color: colors.$text;
          background-color: colors.$alpha-10;
        }
      }

      &.activated {
        background-color: colors.$alpha-10;
        color: colors.$text;
      }
    }

    &.destructive {
      color: colors.$destructive;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: colors.$destructive-hover-light;
        }

        &:active {
          background-color: colors.$destructive-active-light;
        }
      }

      .activated {
        background-color: colors.$destructive-active-light;
      }
    }
  }

  &.text {
    padding: 0;
    line-height: normal;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    text-decoration: underline solid;
    text-underline-offset: 0.15em;
    text-decoration-thickness: 0.1em;

    &[class]:disabled {
      cursor: not-allowed;
      color: colors.$text-disabled;
      opacity: 0.5;
    }

    &.small {
      height: 18px;

      @include typography.small("semi-bold");
    }

    &.medium {
      height: 19px;

      @include typography.medium("semi-bold");
    }

    &.primary {
      color: colors.$main;

      &:not(:disabled) {
        &:hover {
          color: colors.$main-hover;
        }

        &:active {
          color: colors.$icon-inverse;
        }

        &.activated {
          color: colors.$icon-inverse;
        }
      }
    }

    &.grey {
      color: colors.$icon-subtle;

      &:not(:disabled) {
        &:hover {
          color: colors.$icon;
        }

        &:active {
          color: colors.$icon;
        }

        &.activated {
          color: colors.$icon;
        }
      }
    }

    &.destructive {
      color: colors.$destructive;

      &:not(:disabled) {
        &:hover {
          color: colors.$destructive-hover;
        }

        &:active {
          color: colors.$destructive-active;
        }

        &.activated {
          color: colors.$destructive-active;
        }
      }
    }
  }
}
</style>
